/* Veltari Build Stages: package maths and cart payload. No DOM, so it can be unit tested and mirrored in a checkout discount.
 *
 * Rules (all numbers come from the store, nothing is hard-coded here):
 *  - Only "carbon" parts (the main categories) and approved "swap" parts count toward the savings tier.
 *  - The tier is the highest tier whose "parts" is <= the number of counted parts. That percentage comes off every counted part.
 *  - Excluded products are in the build but neither count nor get a discount.
 *  - Exhaust and add-on lines never use the tier. They only get their own offer (percent, optionally capped), 0 by default.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.VltPackageCore = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function isNum(v) { return v !== null && v !== undefined && v !== '' && isFinite(Number(v)); }
  function clampPct(v) { return isNum(v) ? Math.max(0, Math.min(100, Number(v))) : 0; }
  function isCarbon(role) { return role === 'carbon' || role === 'swap'; }

  /* [{parts, percent}] -> clean, ascending by parts, one row per "parts" value (the last one wins). */
  function cleanTiers(raw) {
    var by = {};
    (raw || []).forEach(function (t) {
      if (!t || !isNum(t.parts)) return;
      var parts = Math.floor(Number(t.parts));
      if (parts < 1) return;
      by[parts] = { parts: parts, percent: clampPct(t.percent) };
    });
    return Object.keys(by).map(function (k) { return by[k]; }).sort(function (a, b) { return a.parts - b.parts; });
  }

  /* The tier that applies to n counted parts: the highest tier with parts <= n, else no discount. */
  function tierFor(tiers, n) {
    var best = { parts: 0, percent: 0 };
    cleanTiers(tiers).forEach(function (t) { if (t.parts <= n && t.parts >= best.parts) best = t; });
    return best;
  }

  function maxParts(tiers) {
    var t = cleanTiers(tiers);
    return t.length ? t[t.length - 1].parts : 0;
  }

  /*
   * Where the customer is on the ladder.
   * reachable (optional): the most parts this build could still get to; tiers above it are not offered as the next goal.
   * Returns { counted, percent, tier, max, next: {parts, percent, need} | null, atTop }
   */
  function progress(tiers, counted, reachable) {
    var ts = cleanTiers(tiers);
    var cur = tierFor(ts, counted);
    var next = null;
    for (var i = 0; i < ts.length; i++) {
      var t = ts[i];
      if (t.parts > counted && t.percent > cur.percent && (reachable === undefined || reachable === null || t.parts <= reachable)) { next = t; break; }
    }
    var atTop = !ts.some(function (t) { return t.percent > cur.percent; });
    return {
      counted: counted, percent: cur.percent, tier: cur, max: maxParts(ts),
      next: next ? { parts: next.parts, percent: next.percent, need: next.parts - counted } : null,
      atTop: atTop
    };
  }

  /* An exhaust / add-on offer: percent off, never more than capMinor (minor units) when a cap is set. */
  function ownDiscount(price, percent, capMinor) {
    var p = clampPct(percent);
    if (p <= 0) return 0;
    var d = Math.round(price * p / 100);
    if (isNum(capMinor) && Number(capMinor) >= 0) d = Math.min(d, Math.round(Number(capMinor)));
    return Math.max(0, Math.min(price, d));
  }

  /*
   * cfg:   { tiers: [{parts, percent}], show: bool (savings are live, or this is a preview), missing: [category] (steps with no decision yet) }
   * lines: one per part that will be bought:
   *        { category, role: 'carbon'|'swap'|'exhaust'|'addon', price (minor units), available, excluded, ownPercent, ownCap (minor units or null) }
   */
  function compute(cfg, lines) {
    cfg = cfg || {};
    lines = lines || [];
    var tiers = cleanTiers(cfg.tiers);
    var show = !!cfg.show && tiers.length > 0;
    var missing = (cfg.missing || []).slice();
    var counted = 0, unavailable = [];
    lines.forEach(function (l) {
      if (l.available === false) unavailable.push(l.category);
      if (isCarbon(l.role) && !l.excluded) counted += 1;
    });
    var tier = tierFor(tiers, counted);
    var applied = show ? tier.percent : 0;
    var out = {
      tiers: tiers, show: show, eligible: show,
      missing: missing, unavailable: unavailable,
      complete: missing.length === 0,
      purchasable: missing.length === 0 && lines.length > 0 && unavailable.length === 0,
      counted: counted, tier: tier, percent: applied,
      individual: 0, carbonDiscount: 0, ownDiscount: 0, discount: 0, total: 0, lines: []
    };
    lines.forEach(function (l) {
      var price = Math.max(0, Math.round(Number(l.price) || 0));
      var counts = isCarbon(l.role) && !l.excluded;
      var pct = 0, d = 0, kind = 'none';
      if (show && !l.excluded) {
        if (isCarbon(l.role)) {
          pct = applied; d = Math.round(price * pct / 100); out.carbonDiscount += d; kind = 'tier';
        } else {
          pct = clampPct(l.ownPercent); d = ownDiscount(price, pct, l.ownCap);
          if (d > 0) { out.ownDiscount += d; kind = 'own'; } else { pct = 0; }
        }
      }
      out.individual += price;
      out.discount += d;
      out.lines.push({ category: l.category, role: l.role, price: price, percent: pct, discount: d, final: price - d, counts: counts, excluded: !!l.excluded, kind: kind });
    });
    out.total = out.individual - out.discount;
    return out;
  }

  /* Headline percentage for a stage card: the tier its own carbon parts reach. */
  function stageSaving(tiers, carbonCount, hasOtherParts) {
    return { percent: tierFor(tiers, carbonCount).percent, carbonOnly: !!hasOtherParts };
  }

  function makePackageId() {
    return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  /*
   * Body for POST /cart/add.js. Visible properties tell the customer and the shop what each line is for.
   * Properties starting with "_" are hidden in the cart and are what a checkout discount can read.
   * ctx.lines: [{ variantId, category (the slot it fills), categoryName, role }]
   */
  function buildCartItems(ctx) {
    var label = 'Stage ' + ctx.stage.number + ' · ' + ctx.stage.name;
    return ctx.lines.map(function (l) {
      return {
        id: l.variantId,
        quantity: 1,
        properties: {
          'Build': label,
          'Vehicle': ctx.vehicle.name,
          'Component': l.role === 'swap' ? l.categoryName + ' (swap)' : l.categoryName,
          '_vlt_pkg': ctx.stage.handle,
          '_vlt_pkg_id': ctx.packageId,
          '_vlt_cat': l.category,
          '_vlt_role': l.role,
          '_vlt_veh': ctx.vehicle.handle
        }
      };
    });
  }

  return {
    cleanTiers: cleanTiers, tierFor: tierFor, maxParts: maxParts, progress: progress, ownDiscount: ownDiscount,
    compute: compute, stageSaving: stageSaving, makePackageId: makePackageId, buildCartItems: buildCartItems
  };
});
